import React, { useMemo } from 'react';
import {
  ThemeProvider,
  ScopedCssBaseline,
  Box,
  createTheme,
} from '@mui/material';
import { ruRU, esES, frFR } from '@mui/material/locale';
import { darkTheme, lightTheme } from './custom-mui-theme';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { type SupportedLanguage } from 'store/language.slice';

interface CustomMuiThemeProviderProps {
  children: React.ReactNode;
}

const MUI_LOCALE_MAP = {
  en: {},
  ru: ruRU,
  es: esES,
  fr: frFR,
} satisfies Record<SupportedLanguage, object>;

/**
 * CustomMuiThemeProvider
 *
 * NOTE:
 * - This is a *local theme wrapper* for new development.
 * - Do NOT rely on the global theme (it's inconsistent).
 * - All new screens/components should wrap inside this provider
 *   to get predictable dark/light theme behavior.
 * - Extend colors/overrides ONLY via `lightTheme` / `darkTheme`
 *   (see theme.ts), not by hardcoding hex values in components.
 *
 * Usage:
 * <CustomMuiThemeProvider>
 *   <MyNewFeature />
 * </CustomMuiThemeProvider>
 *
 * Migration strategy:
 * - Legacy screens can stay on the global theme.
 * - When updating legacy code, migrate them here incrementally.
 */
export const CustomMuiThemeProvider = ({
  children,
}: CustomMuiThemeProviderProps) => {
  const { enableDarkTheme } = useAppTheme();
  const language = useAppSelector((state) => state.languageReducer.language);

  const localizedTheme = useMemo(
    () =>
      createTheme(
        enableDarkTheme ? darkTheme : lightTheme,
        MUI_LOCALE_MAP[language]
      ),
    [enableDarkTheme, language]
  );

  return (
    <ThemeProvider theme={localizedTheme}>
      <ScopedCssBaseline sx={{ backgroundColor: 'transparent' }}>
        <Box sx={{ height: '100%', width: '100%' }}>{children}</Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
};
